import type { Response } from "./types";

/**
 * Returns true if the response should be shown on stage and in the AI pipeline.
 *
 * Visibility rules:
 * - Hidden responses are never visible.
 * - moderation_overridden: the facilitator has manually classified this response.
 *   A clean/borderline override is visible; a block override is NEVER visible
 *   (block always wins regardless of the override flag).
 * - "clean" → visible.
 * - "borderline" → visible only once borderline_until has passed (30-second grace).
 * - "pending" and "block" → hidden.
 *
 * The optional `now` parameter exists for testing; pass a fixed Date to verify
 * borderline time-window logic without actual clock dependency.
 */
export function isVisibleResponse(r: Response, now?: Date): boolean {
  const _now = now ?? new Date();
  if (r.is_hidden) return false;
  if (r.moderation_overridden) return r.moderation_status !== "block"; // never override a block
  if (r.moderation_status === "clean") return true;
  if (r.moderation_status === "borderline") {
    if (!r.borderline_until) return false;
    return new Date(r.borderline_until).getTime() <= _now.getTime();
  }
  return false; // "pending" and "block" stay hidden
}

/**
 * Alias of isVisibleResponse: true only for clean + post-grace-period borderline.
 * Used as the filter for clustering/AI input to ensure only safe content
 * is passed to the AI pipeline.
 */
export function isPublicResponse(r: Response, now?: Date): boolean {
  return isVisibleResponse(r, now);
}

/**
 * True when a response is eligible for live placement: visible (passes moderation
 * gate) AND not yet placed into a cluster. Used by `useLivePlacement` to filter
 * which responses to send to the edge function. Kept separate from
 * isVisibleResponse so future placement-specific rules (e.g. minimum text
 * length) can be added without affecting visibility semantics.
 */
export function isPlaceable(r: Response, now?: Date): boolean {
  if (r.cluster_id != null) return false;
  return isVisibleResponse(r, now);
}

/**
 * Stable pseudonymous respondent labels for exports: the person whose first
 * answer arrived earliest becomes "R1", the next "R2", and so on. Numbering
 * is deterministic for a given dataset, so re-exports keep their labels. The
 * respondent keys themselves stay with the caller; only the labels leave.
 */
export function respondentNumbers(
  rows: Array<{ respondent_key: string; submitted_at: string }>,
): Map<string, string> {
  const firstSeen = new Map<string, string>();
  for (const r of rows) {
    const prev = firstSeen.get(r.respondent_key);
    if (prev === undefined || r.submitted_at < prev) firstSeen.set(r.respondent_key, r.submitted_at);
  }
  const ordered = [...firstSeen.entries()].sort(
    (a, b) => a[1].localeCompare(b[1]) || a[0].localeCompare(b[0]),
  );
  return new Map(ordered.map(([key], i) => [key, `R${i + 1}`]));
}
